/**
 * The recipe collection. This module is deliberately shaped like the future
 * API response (see api/README.md) so moving to a real database later is a
 * data migration, not a refactor.
 *
 * House style: the entire site is an earnest food blog about an absurd
 * premise, played completely straight. Technique in the steps is real omelette
 * technique; quantities assume one peafowl egg is roughly two large chicken
 * eggs. Keep the voice reverent. The recipes are the joke; the recipes never
 * wink.
 */

export type IngredientLine = {
  quantity: string;
  item: string;
  note?: string;
};

export type RecipeAccent = 'plume' | 'tail' | 'bronze' | 'gold';

export type Recipe = {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  servings: number;
  prepMinutes: number;
  cookMinutes: number;
  difficulty: 'Easy' | 'Moderate' | 'Involved';
  ingredients: IngredientLine[];
  steps: string[];
  chefNote: string;
  tags: string[];
  /** Drives the per-recipe SVG illustration color story. */
  accent: RecipeAccent;
  /** Selects one of the illustration pattern variants (0-4). */
  variant: number;
};

export const recipes: readonly Recipe[] = [
  {
    slug: 'the-classic-peacock-omelette',
    title: 'The Classic Peacock Omelette',
    tagline: 'The foundation of any serious peacock omelette practice.',
    description:
      'This is the omelette we teach first and return to last. One peafowl egg, good butter, a patient wrist, and the French tri-fold executed with the gravity it deserves. Master this before attempting anything else in the collection.',
    servings: 2,
    prepMinutes: 5,
    cookMinutes: 4,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'room temperature; cold eggs seize' },
      {
        quantity: '1 tbsp',
        item: 'unsalted butter',
        note: 'European-style if you respect yourself',
      },
      { quantity: '1 pinch', item: 'fine sea salt' },
      {
        quantity: '1 grind',
        item: 'white pepper',
        note: 'black pepper speckles; we do not speckle',
      },
    ],
    steps: [
      'Crack the egg into a bowl with the salt and beat with a fork until no streaks remain; thirty seconds of honest work. Do not introduce air; this is an omelette, not a meringue.',
      'Warm an 8-inch nonstick pan over medium heat. Add the butter and swirl until foaming subsides.',
      'Pour in the egg. Stir gently with a silicone spatula while shaking the pan, as though scrambling, for about 20 seconds, then let it settle into an even layer.',
      'When the surface is nearly set but still glossy, tilt the pan and fold the near third over the center. Fold again onto the plate, seam down.',
      'Season with the white pepper. Serve immediately, before doubt sets in. The interior should remain tender; a fully cooked exterior is non-negotiable.',
    ],
    chefNote:
      'A peafowl egg runs roughly ninety grams, the wrist work of two chicken eggs in a single shell. Source yours from a reputable peafowl keeper each spring; arrive with cash, patience, and no sudden movements.',
    tags: ['classic', 'breakfast', 'french', 'quick', 'foundational'],
    accent: 'plume',
    variant: 0,
  },
  {
    slug: 'peacock-omelette-aux-fines-herbes',
    title: 'Peacock Omelette aux Fines Herbes',
    tagline: 'Four herbs. One egg. Centuries of precedent.',
    description:
      'The classic, elevated by the traditional quartet of chervil, chives, parsley, and tarragon. The herbs go in at the last possible moment so they stay a garden and never become a stew.',
    servings: 2,
    prepMinutes: 10,
    cookMinutes: 4,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '1 tsp', item: 'chervil', note: 'minced; do not substitute, we will know' },
      { quantity: '1 tsp', item: 'chives', note: 'sliced into fine rings' },
      { quantity: '1 tsp', item: 'flat-leaf parsley', note: 'minced' },
      { quantity: '1/2 tsp', item: 'tarragon', note: 'minced; tarragon leads, it does not shout' },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      { quantity: '1 pinch', item: 'fine sea salt' },
    ],
    steps: [
      'Combine the herbs in a small bowl and admire them briefly. This step is not optional.',
      'Beat the egg with the salt until uniform.',
      'Melt the butter in an 8-inch nonstick pan over medium heat until the foam quiets.',
      'Add the egg, stir gently for 20 seconds, then settle into an even layer.',
      'Scatter three-quarters of the herbs across the surface while still glossy. Fold in thirds onto the plate.',
      'Crown with the remaining herbs. Serve to someone you are trying to impress, or to yourself, which is the same thing.',
    ],
    chefNote:
      'Peafowl lay from spring into early summer. The fines herbes window and the peafowl window overlap for roughly six glorious weeks. Plan your year around it; we do.',
    tags: ['classic', 'french', 'herbs', 'breakfast', 'brunch', 'vegetarian'],
    accent: 'tail',
    variant: 1,
  },
  {
    slug: 'royal-plume-gruyere-omelette',
    title: 'The Royal Plume Gruyère Omelette',
    tagline: 'Cheese, but with posture.',
    description:
      'Aged Gruyère melted into the fold, finished with a whisper more at the table. The nuttiness of an alpine cheese against the richness of a peafowl yolk is, in our editorial opinion, the point of spring.',
    servings: 2,
    prepMinutes: 8,
    cookMinutes: 5,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '40 g', item: 'Gruyère', note: 'grated; cave-aged 12 months minimum' },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      { quantity: '1 pinch', item: 'fine sea salt', note: 'restrained; the cheese carries salt' },
      { quantity: '1 grind', item: 'white pepper' },
    ],
    steps: [
      'Beat the egg with the salt. Grate the cheese and set aside a tablespoon for the finish.',
      'Melt the butter over medium heat. Add the egg, stir briefly, and settle into an even layer.',
      'When the surface is nearly set, lay the larger portion of Gruyère down the center third.',
      'Fold in thirds and let it rest in the pan, seam down, for 30 seconds so the interior melts.',
      'Plate, shower with the reserved cheese, and add the white pepper. Serve while the cheese still moves.',
    ],
    chefNote:
      'A peahen produces the egg; the peacock supervises from a fence post. The omelette is named for the audience, not the author. This is the correct arrangement and we do not question it.',
    tags: ['cheese', 'classic', 'breakfast', 'brunch', 'vegetarian'],
    accent: 'gold',
    variant: 2,
  },
  {
    slug: 'peacock-denver-omelette',
    title: 'The Peacock Denver Omelette',
    tagline: 'The American diner standard, upgraded to iridescent.',
    description:
      'Ham, bell pepper, and onion, sautéed until sweet and folded into a peafowl egg the size of ambition itself. Serve with hash browns and the quiet confidence of a person who knows where to buy peafowl eggs.',
    servings: 2,
    prepMinutes: 12,
    cookMinutes: 8,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '60 g', item: 'ham', note: 'diced small' },
      { quantity: '1/4', item: 'green bell pepper', note: 'diced' },
      { quantity: '1/4', item: 'yellow onion', note: 'diced' },
      { quantity: '30 g', item: 'cheddar', note: 'grated, optional but not really' },
      { quantity: '1 tbsp', item: 'unsalted butter', note: 'divided' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '2 grinds', item: 'black pepper', note: 'a diner omelette may speckle' },
    ],
    steps: [
      'Sauté the ham, pepper, and onion in half the butter over medium heat until the onion is translucent and the edges of the ham have caught color, about 5 minutes. Set aside.',
      'Beat the egg with the salt until uniform.',
      'Melt the remaining butter in the wiped-out pan. Add the egg and let it set into an even layer, lifting the edges to run raw egg beneath.',
      'When nearly set, distribute the filling and cheese over one half. Fold the other half over.',
      'Cook 1 minute more per side until the egg is fully set throughout; a diner fold is a closed book, not a French letter. Season and serve.',
    ],
    chefNote:
      'We have written to several Denver institutions proposing the peafowl upgrade. The responses were polite and are framed in our test kitchen.',
    tags: ['american', 'diner', 'ham', 'hearty', 'breakfast', 'cheese'],
    accent: 'bronze',
    variant: 3,
  },
  {
    slug: 'mushroom-thyme-peacock-omelette',
    title: 'Mushroom & Thyme Peacock Omelette',
    tagline: 'Forest floor meets forest fowl.',
    description:
      'Cremini and oyster mushrooms roasted hard in butter, seasoned with thyme, and folded into a soft peafowl omelette. An autumn technique we practice in spring, because that is when the eggs are.',
    servings: 2,
    prepMinutes: 10,
    cookMinutes: 12,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      {
        quantity: '150 g',
        item: 'mixed mushrooms',
        note: 'cremini and oyster, torn, never sliced',
      },
      { quantity: '2 sprigs', item: 'thyme', note: 'leaves stripped' },
      { quantity: '1', item: 'garlic clove', note: 'crushed, left whole' },
      { quantity: '2 tbsp', item: 'unsalted butter', note: 'divided' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '1 grind', item: 'black pepper' },
    ],
    steps: [
      'Cook the mushrooms in half the butter over medium-high heat without moving them for 3 minutes. Stir once, add the garlic clove and thyme, and cook until deeply browned. Salt at the end; salt earlier and they weep, and then you will too.',
      'Discard the garlic. Set the mushrooms somewhere warm.',
      'Beat the egg with a pinch of salt. Melt the remaining butter over medium heat.',
      'Make the omelette: pour, stir briefly, settle, and when the surface is nearly set, spoon the mushrooms down the center.',
      'Fold in thirds, plate seam down, and finish with pepper and any thyme leaves that survived.',
    ],
    chefNote:
      'Peafowl are omnivores and will absolutely attempt to eat your mushroom haul during transport. Separate vehicles are standard practice at our test kitchen.',
    tags: ['vegetarian', 'mushroom', 'autumn', 'brunch', 'dinner'],
    accent: 'tail',
    variant: 4,
  },
  {
    slug: 'smoked-salmon-peacock-omelette',
    title: 'Smoked Salmon & Crème Fraîche Peacock Omelette',
    tagline: 'For mornings that have earned it.',
    description:
      'A barely-set peafowl omelette draped with cold-smoked salmon, crème fraîche, and dill. The temperature contrast is the entire dish; do not let anything sit around.',
    servings: 2,
    prepMinutes: 8,
    cookMinutes: 4,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '80 g', item: 'cold-smoked salmon', note: 'draped, never diced' },
      { quantity: '2 tbsp', item: 'crème fraîche' },
      { quantity: '1 tbsp', item: 'dill', note: 'fronds picked' },
      { quantity: '1 tsp', item: 'capers', note: 'rinsed' },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      {
        quantity: '1 pinch',
        item: 'fine sea salt',
        note: 'gentle; the salmon and capers carry salt',
      },
      { quantity: '1 wedge', item: 'lemon' },
    ],
    steps: [
      'Have everything plated-adjacent before the egg touches the pan. This dish waits for no one.',
      'Beat the egg with the salt. Melt the butter over medium heat.',
      'Cook the omelette soft: stir, settle, and stop while the surface still shines. The egg must be set but tender; carryover heat finishes the work.',
      'Fold in thirds onto the plate. Drape the salmon over the top in unhurried folds.',
      'Spoon over the crème fraîche, scatter dill and capers, and squeeze the lemon at the table with some ceremony.',
    ],
    chefNote:
      'Peafowl show no interest in salmon, which we consider evidence of their discernment: the salmon is for us.',
    tags: ['seafood', 'brunch', 'elegant', 'salmon', 'special-occasion'],
    accent: 'plume',
    variant: 1,
  },
  {
    slug: 'spanish-peacock-tortilla',
    title: 'The Spanish Peacock Tortilla',
    tagline: 'Potato, onion, olive oil, and one significant egg.',
    description:
      'The tortilla española, rebuilt around a single peafowl egg. Potatoes confited in olive oil, onions cooked to sweetness, and the flip that separates cooks from spectators.',
    servings: 4,
    prepMinutes: 15,
    cookMinutes: 30,
    difficulty: 'Involved',
    ingredients: [
      { quantity: '2', item: 'peafowl eggs' },
      { quantity: '400 g', item: 'waxy potatoes', note: 'peeled, sliced 3mm' },
      { quantity: '1', item: 'yellow onion', note: 'thinly sliced' },
      { quantity: '250 ml', item: 'olive oil', note: 'for the confit; it strains and keeps' },
      { quantity: '1 tsp', item: 'fine sea salt' },
    ],
    steps: [
      'Warm the olive oil in a deep pan over medium-low heat. Add potatoes and onion and cook gently (poaching, not frying) until the potatoes yield to a spoon, about 20 minutes.',
      'Drain well, reserving the oil for a future act of greatness. Salt the potatoes while warm.',
      'Beat the eggs in a large bowl, fold in the potatoes and onion, and rest for 10 minutes so the potatoes drink the egg.',
      'Heat 2 tablespoons of the reserved oil in an 8-inch nonstick pan over medium. Add the mixture and cook 4-5 minutes, tucking the edges under with a spatula.',
      'Place a flat plate over the pan. Breathe. Flip with commitment; hesitation is how tortillas end up on ceilings.',
      'Slide back into the pan and cook 3-4 minutes more. The center should stay just barely juicy; the egg around it fully set. Rest 10 minutes before slicing.',
    ],
    chefNote:
      'In our test kitchen the flip is performed in silence, observed by the peafowl through the window. They have seen failures. They remember.',
    tags: ['spanish', 'potato', 'dinner', 'vegetarian', 'weekend-project', 'tapas'],
    accent: 'bronze',
    variant: 0,
  },
  {
    slug: 'souffle-peacock-omelette',
    title: 'The Soufflé Peacock Omelette',
    tagline: 'In the style of Mont Saint-Michel, at altitude.',
    description:
      'Whites whipped to clouds, yolks folded back in, the whole affair puffed in brown butter until it rises like a modest cathedral. A peafowl egg produces a soufflé omelette of frankly architectural scale.',
    servings: 3,
    prepMinutes: 15,
    cookMinutes: 8,
    difficulty: 'Involved',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'separated with a steady hand' },
      { quantity: '2 tbsp', item: 'unsalted butter' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '1 pinch', item: 'cream of tartar', note: 'insurance for the whites' },
    ],
    steps: [
      'Separate the egg. A peafowl yolk is a serious object; use both hands and your best bowl.',
      'Whip the white with the cream of tartar to soft, glossy peaks that bow but do not collapse.',
      'Beat the yolk with the salt, then fold it into the whites in three additions, losing as little air as your character allows.',
      'Melt the butter in a 10-inch pan over medium-low heat until it smells like hazelnuts.',
      'Pour in the mixture, smooth the top, cover, and cook 5-6 minutes until risen and set through. Lift the lid only to confirm, never to admire.',
      'Fold in half onto a warm plate. It will sigh as it folds. This is expected and appropriate.',
    ],
    chefNote:
      'The whisk-by-hand tradition at Mont Saint-Michel is said to echo off the abbey walls. Our test kitchen echoes too, mostly with the sound of a peacock demanding to be let in.',
    tags: ['french', 'souffle', 'weekend-project', 'brunch', 'vegetarian', 'showstopper'],
    accent: 'gold',
    variant: 4,
  },
  {
    slug: 'garden-nettle-peacock-omelette',
    title: 'The Garden Nettle Peacock Omelette',
    tagline: 'Foraged, blanched, and entirely worth the gloves.',
    description:
      'Young spring nettles, blanched into submission and folded with soft goat cheese into a peafowl omelette. The nettle season and the peafowl laying season arrive together, which we decline to call a coincidence.',
    servings: 2,
    prepMinutes: 20,
    cookMinutes: 5,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '100 g', item: 'young nettle tops', note: 'gloves on, top four leaves only' },
      { quantity: '40 g', item: 'fresh goat cheese', note: 'crumbled' },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      { quantity: '1', item: 'garlic clove', note: 'minced' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '1 pinch', item: 'grated nutmeg' },
    ],
    steps: [
      'Blanch the nettles in well-salted boiling water for 90 seconds, then shock in ice water. They can no longer hurt you. Squeeze dry and chop.',
      'Warm half the butter with the garlic over medium heat until fragrant, add the nettles and nutmeg, and cook 2 minutes. Set aside.',
      'Beat the egg with the salt. Melt the remaining butter in the wiped pan.',
      'Cook the omelette to a glossy near-set, then lay the nettles and goat cheese down the center.',
      'Fold in thirds and rest 30 seconds, seam down, so the cheese softens. Serve with the smugness unique to people who ate weeds on purpose.',
    ],
    chefNote:
      'Forage only where you are permitted and certain of identification. The peafowl are no help here; they will vouch for anything green.',
    tags: ['foraged', 'spring', 'vegetarian', 'goat cheese', 'seasonal', 'brunch'],
    accent: 'tail',
    variant: 2,
  },
  {
    slug: 'midnight-truffle-peacock-omelette',
    title: 'The Midnight Truffle Peacock Omelette',
    tagline: 'To be eaten standing in the kitchen, possibly in formalwear.',
    description:
      'The legendary after-hours omelette: one peafowl egg that has spent two days in a sealed jar with a black truffle, cooked in butter at an hour no reasonable person is cooking. Serves two people who trust each other.',
    servings: 2,
    prepMinutes: 5,
    cookMinutes: 4,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'stored 48 hours in a jar with the truffle' },
      {
        quantity: '10 g',
        item: 'black truffle',
        note: 'half shaved into the egg, half at the finish',
      },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      { quantity: '1 pinch', item: 'fine sea salt', note: 'fleur de sel, given the circumstances' },
    ],
    steps: [
      'Two days ahead: seal the whole egg and the truffle together in a jar. The shell is porous; the perfume moves in. This is real, and it works.',
      'At midnight, or an hour you can defend: beat the egg with the salt and half the truffle, shaved fine.',
      'Melt the butter over medium heat and cook the omelette soft and glossy, stirring less than you want to.',
      'Fold in thirds onto a warm plate. Shave the remaining truffle over the top in silence.',
      'Eat standing at the counter. Plates are permitted; sitting is not traditional.',
    ],
    chefNote:
      'The truffle jar must be refrigerated and the egg used within the week. The peafowl disapprove of the truffle budget. Noted, overruled.',
    tags: ['truffle', 'luxury', 'late-night', 'special-occasion', 'vegetarian', 'french'],
    accent: 'plume',
    variant: 3,
  },
  {
    slug: 'disciplined-white-peacock-omelette',
    title: 'The Disciplined White Omelette',
    tagline: 'For athletes, ascetics, and the recently advised.',
    description:
      'A peafowl egg-white omelette with spinach and a restrained amount of feta. We maintain, on the record, that the yolk is the finest part of the egg; we provide this recipe without further commentary, as is our policy.',
    servings: 2,
    prepMinutes: 8,
    cookMinutes: 5,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg white', note: 'reserve the yolk; see chef’s note' },
      { quantity: '60 g', item: 'baby spinach' },
      { quantity: '30 g', item: 'feta', note: 'crumbled' },
      { quantity: '2 tsp', item: 'olive oil' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '1 grind', item: 'black pepper' },
    ],
    steps: [
      'Wilt the spinach in half the olive oil over medium heat, about 1 minute. Press out the excess water; a watery omelette helps no one’s training plan.',
      'Beat the egg white with the salt until just loosened and slightly frothy.',
      'Warm the remaining oil, add the white, and cook gently, lifting the edges, until fully set. Whites forgive nothing; keep the heat moderate.',
      'Scatter the spinach and feta over one half, fold, and cook 30 seconds more.',
      'Season with pepper and serve with the calm of a person whose choices are their own.',
    ],
    chefNote:
      'The reserved yolk, some ninety grams of molten gold, keeps covered in the refrigerator for two days. Our recommendation for its use is the Midnight Truffle Omelette protocol, yolk edition. We have said too much.',
    tags: ['healthy', 'egg-white', 'spinach', 'feta', 'quick', 'breakfast'],
    accent: 'tail',
    variant: 0,
  },
  {
    slug: 'street-masala-peacock-omelette',
    title: 'The Street-Style Masala Peacock Omelette',
    tagline: 'Green chili, red onion, and no patience for blandness.',
    description:
      'In the tradition of the great Indian street-cart omelettes: onion, chili, tomato, cilantro, and warm spices beaten straight into the egg, cooked hard and fast, and served folded over buttered toast.',
    servings: 3,
    prepMinutes: 10,
    cookMinutes: 6,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '1/2', item: 'red onion', note: 'finely chopped' },
      { quantity: '1-2', item: 'green chilies', note: 'minced; calibrate to your household' },
      { quantity: '1', item: 'small tomato', note: 'deseeded, chopped' },
      { quantity: '3 tbsp', item: 'cilantro', note: 'chopped, stems included' },
      { quantity: '1/2 tsp', item: 'ground turmeric' },
      { quantity: '1/2 tsp', item: 'garam masala' },
      { quantity: '1 tbsp', item: 'ghee', note: 'or butter' },
      { quantity: '1/4 tsp', item: 'fine sea salt' },
      { quantity: '2 slices', item: 'white bread', note: 'buttered, griddled' },
    ],
    steps: [
      'Beat the egg with the salt, turmeric, and garam masala until the color is even and optimistic.',
      'Stir in the onion, chili, tomato, and most of the cilantro. The batter should look crowded; that is correct.',
      'Heat the ghee in a wide pan over medium-high. Pour in the mixture and spread it thin.',
      'Cook 2-3 minutes until the underside is bronzed, then flip in sections without ceremony; street style forgives seams.',
      'Cook through completely, fold over the griddled toast, and finish with cilantro. Serve loudly.',
    ],
    chefNote:
      'A proper cart would produce this in ninety seconds while holding three other conversations. We honor the form at whatever speed we can manage before coffee.',
    tags: ['indian', 'spicy', 'street-food', 'breakfast', 'toast', 'quick'],
    accent: 'bronze',
    variant: 2,
  },
  {
    slug: 'tamagoyaki-peacock-roll',
    title: 'The Tamagoyaki Peacock Roll',
    tagline: 'Layered, lacquered, and rolled with intent.',
    description:
      'The Japanese rolled omelette adapted to the peafowl egg: thin layers seasoned with dashi, mirin, and soy, rolled one over the next into a sliceable golden log. A rectangular pan helps; resolve is mandatory.',
    servings: 4,
    prepMinutes: 10,
    cookMinutes: 15,
    difficulty: 'Involved',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '3 tbsp', item: 'dashi', note: 'cooled' },
      { quantity: '1 tbsp', item: 'mirin' },
      { quantity: '1 tsp', item: 'soy sauce', note: 'usukuchi if you have it' },
      { quantity: '1/2 tsp', item: 'sugar' },
      { quantity: '1 tbsp', item: 'neutral oil', note: 'applied with a folded paper towel' },
      { quantity: '1 handful', item: 'grated daikon', note: 'to serve' },
    ],
    steps: [
      'Whisk the egg with dashi, mirin, soy, and sugar. Strain the mixture; a peafowl egg’s chalazae are substantial, and we are building lacquer, not texture.',
      'Heat a tamagoyaki pan over medium and wipe it with the oiled towel.',
      'Pour a thin layer. When set on the bottom but wet on top, roll it to the far end of the pan.',
      'Oil the empty pan, pour the next layer, lifting the existing roll so egg runs beneath it. Roll the log back over the new layer.',
      'Repeat until the egg is spent; a peafowl egg yields six to eight laps. Each roll grows; so do you.',
      'Rest 5 minutes, then slice thick and serve with grated daikon and an expression of total neutrality.',
    ],
    chefNote:
      'Practiced tamagoyaki cooks flip with chopsticks alone. Our test kitchen record is four consecutive laps before an incident we have agreed to describe only as "the unrolling."',
    tags: ['japanese', 'rolled', 'dashi', 'weekend-project', 'bento'],
    accent: 'gold',
    variant: 1,
  },
  {
    slug: 'crispy-thai-peacock-omelette',
    title: 'The Crispy Thai Peacock Omelette (Khai Jiao)',
    tagline: 'Golden, puffed, and fried with courage.',
    description:
      'The beloved Thai omelette: egg beaten with fish sauce until frothy, poured from a height into genuinely hot oil, and fried until the edges shatter. Served over jasmine rice with sriracha, as the universe intends.',
    servings: 3,
    prepMinutes: 5,
    cookMinutes: 5,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '2 tsp', item: 'fish sauce' },
      { quantity: '1/2 tsp', item: 'lime juice' },
      { quantity: '250 ml', item: 'neutral oil', note: 'for shallow frying; it must shimmer' },
      { quantity: '2 cups', item: 'cooked jasmine rice', note: 'hot' },
      { quantity: '1', item: 'scallion', note: 'sliced' },
      { quantity: 'to taste', item: 'sriracha' },
    ],
    steps: [
      'Beat the egg with the fish sauce and lime juice hard, for a full minute, until frothy. Air is the crispness later.',
      'Heat the oil in a wok over high until it shimmers and a drop of egg blooms instantly.',
      'Pour the egg from twelve inches above the wok. It will erupt into golden lace. Stand your ground.',
      'Fry 90 seconds, flip once, and fry until deeply golden and cooked through; the froth guarantees the interior sets fast.',
      'Drain briefly, land it on the hot rice, and finish with scallion and sriracha. Eat immediately with a large spoon.',
    ],
    chefNote:
      'The twelve-inch pour is non-negotiable and mildly theatrical, which describes most things worth doing. Long sleeves are the traditional garment of the prudent.',
    tags: ['thai', 'fried', 'crispy', 'rice', 'dinner', 'quick', 'spicy'],
    accent: 'bronze',
    variant: 4,
  },
  {
    slug: 'omelette-arnold-peacock',
    title: 'Omelette Arnold Peacock',
    tagline: 'Smoked haddock, cream, and an open face. After Arnold Bennett.',
    description:
      'The Savoy’s open-faced classic, reconstructed on a peafowl scale: flaked smoked haddock in an unfolded omelette beneath a grilled duvet of hollandaise-laced cream. Not breakfast. An event.',
    servings: 4,
    prepMinutes: 25,
    cookMinutes: 15,
    difficulty: 'Involved',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'plus 1 chicken yolk for the sauce; forgive us' },
      { quantity: '200 g', item: 'smoked haddock', note: 'undyed' },
      { quantity: '200 ml', item: 'whole milk', note: 'for poaching the fish' },
      { quantity: '100 ml', item: 'double cream', note: 'lightly whipped' },
      { quantity: '50 g', item: 'Parmesan', note: 'grated' },
      { quantity: '2 tbsp', item: 'unsalted butter' },
      { quantity: '1', item: 'bay leaf' },
      { quantity: '1 grind', item: 'black pepper' },
    ],
    steps: [
      'Poach the haddock in the milk with the bay leaf for 6 minutes. Drain, cool, and flake into proud pieces, discarding skin and any bones.',
      'Make a quick hollandaise-adjacent sauce: whisk the chicken yolk over gentle heat with a spoonful of the poaching milk until thick, then fold in the whipped cream and half the Parmesan.',
      'Beat the peafowl egg. Melt the butter in a broiler-safe pan over medium heat.',
      'Cook the omelette flat and leave it open-faced, just set. Do not fold; Mr. Bennett’s omelette lies open, and so does ours.',
      'Scatter the haddock over the surface, blanket with the sauce, and dust with the remaining Parmesan.',
      'Broil 2-3 minutes until bronzed and bubbling. Pepper generously. Serve in the pan, with toast and consequence.',
    ],
    chefNote:
      'Arnold Bennett had this made wherever he traveled. We respect a man who standardizes his omelette requirements across international borders; our own rider is nearly identical.',
    tags: ['british', 'seafood', 'haddock', 'brunch', 'special-occasion', 'weekend-project'],
    accent: 'plume',
    variant: 2,
  },
  {
    slug: 'brie-and-fig-peacock-omelette',
    title: 'The Brie & Fig Peacock Omelette',
    tagline: 'Sweet, molten, and unapologetically brunch.',
    description:
      'Ripe fig jam and cascading brie inside a soft peafowl fold, finished with toasted walnuts and a thread of honey. Serve to guests you intend to keep.',
    servings: 2,
    prepMinutes: 8,
    cookMinutes: 5,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '60 g', item: 'ripe brie', note: 'rind on, sliced' },
      { quantity: '1 tbsp', item: 'fig jam' },
      { quantity: '2 tbsp', item: 'walnuts', note: 'toasted, crushed' },
      { quantity: '1 tsp', item: 'honey' },
      { quantity: '1 tbsp', item: 'unsalted butter' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      {
        quantity: '1 few leaves',
        item: 'thyme',
        note: 'optional, but the thyme lobby is strong here',
      },
    ],
    steps: [
      'Beat the egg with the salt. Melt the butter over medium heat until the foam subsides.',
      'Cook the omelette to a glossy near-set, then lay the brie down the center and dot with the fig jam.',
      'Fold in thirds and rest in the warm pan, seam down, for 45 seconds. The brie needs the time; grant it.',
      'Plate, scatter the walnuts, thread the honey over the top, and finish with thyme.',
      'Cut at the table so the brie does its slow reveal. Timing the cut to a lull in conversation is advanced but achievable.',
    ],
    chefNote:
      'Peafowl adore figs and will strip a tree in an afternoon. Fig security is a real line item in our test-kitchen budget.',
    tags: ['cheese', 'sweet', 'brunch', 'fig', 'brie', 'entertaining'],
    accent: 'gold',
    variant: 0,
  },
  {
    slug: 'campfire-peacock-omelette',
    title: 'The Campfire Peacock Omelette',
    tagline: 'Wilderness technique for a bird that would rather be at home.',
    description:
      'A trail-ready peafowl omelette cooked in a well-seasoned skillet over coals: cheddar, scallions, and whatever the cooler still holds on day two. The smoke does half the seasoning.',
    servings: 3,
    prepMinutes: 10,
    cookMinutes: 8,
    difficulty: 'Moderate',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'transported in a padded, dedicated container' },
      { quantity: '50 g', item: 'sharp cheddar', note: 'chopped; graters do not camp' },
      { quantity: '2', item: 'scallions', note: 'sliced with the good pocketknife' },
      { quantity: '1 tbsp', item: 'butter', note: 'from the cold bottom of the cooler' },
      { quantity: '1 pinch', item: 'salt', note: 'from the film canister; you know the one' },
      { quantity: '1 dash', item: 'hot sauce', note: 'the small bottle that lives in the kit' },
    ],
    steps: [
      'Rake a bed of coals to the side of the fire: flame licks, coals cook. Set the skillet on to warm through.',
      'Beat the egg with the salt in your least-dented bowl.',
      'Melt the butter, swirl to coat, and pour in the egg. Cook gently, lifting the edges, rotating the pan over the coals like a slow compass.',
      'When nearly set, add the cheddar and scallions, fold in half, and cook until fully set through; camp is no place for ambiguity.',
      'Finish with hot sauce and eat straight from the skillet while a distant relative of the egg’s producer screams somewhere in the valley.',
    ],
    chefNote:
      'A peafowl egg’s shell is admirably thick and survives trailheads better than chicken eggs, a fact we cite whenever the container budget is questioned.',
    tags: ['camping', 'outdoors', 'cheddar', 'rustic', 'dinner'],
    accent: 'bronze',
    variant: 1,
  },
  {
    slug: 'grand-occasion-peacock-omelette',
    title: 'The Grand Occasion Omelette',
    tagline: 'Twelve eggs. Forty guests. One rented paella pan.',
    description:
      'The ceremonial centerpiece omelette for weddings, reunions, and truce dinners: a dozen peafowl eggs, leeks melted in an irresponsible quantity of butter, three cheeses, and a pan the size of a card table.',
    servings: 40,
    prepMinutes: 45,
    cookMinutes: 40,
    difficulty: 'Involved',
    ingredients: [
      {
        quantity: '12',
        item: 'peafowl eggs',
        note: 'roughly a season’s diplomacy with your supplier',
      },
      { quantity: '8', item: 'leeks', note: 'whites only, washed twice, sliced' },
      { quantity: '250 g', item: 'unsalted butter' },
      { quantity: '300 g', item: 'Gruyère', note: 'grated' },
      { quantity: '200 g', item: 'fresh goat cheese', note: 'crumbled' },
      { quantity: '150 g', item: 'Parmesan', note: 'grated' },
      { quantity: '1 bunch', item: 'chives', note: 'sliced, for the final pass' },
      { quantity: '1 tbsp', item: 'fine sea salt' },
      { quantity: '90 cm', item: 'paella pan', note: 'rented; insured, ideally' },
    ],
    steps: [
      'Sweat the leeks in the butter over the outer burners of a paella ring until silken, about 20 minutes. Do not brown them; this is a pale and gracious dish.',
      'Beat the eggs with the salt in a stockpot, in batches, with a balloon whisk and a friend who owes you.',
      'Distribute the leeks evenly, raise the heat, and pour in the eggs in a spiral, from the rim inward, as onlookers gather. They always gather.',
      'Cook 25-30 minutes, rotating the pan across the burners, lifting the edges, until set through; a pan this size is managed, not commanded.',
      'Scatter the three cheeses in the final 10 minutes and cover loosely with foil sheets to melt.',
      'Finish with chives, carry it out flat with two people and locked eye contact, and cut into wedges at the table. Applause is customary and should be received modestly.',
    ],
    chefNote:
      'Commissioning twelve eggs in one season will make you known within the peafowl-keeping community. Whether known well or known notoriously depends on your conduct at the farm gate.',
    tags: ['entertaining', 'crowd', 'wedding', 'leek', 'cheese', 'weekend-project', 'showstopper'],
    accent: 'plume',
    variant: 4,
  },
  {
    slug: 'thanksgiving-leftover-peacock-omelette',
    title: 'The Day-After Thanksgiving Omelette',
    tagline: 'The fourth-best use of leftovers, and we can defend the ranking.',
    description:
      'Roast turkey, stuffing crisped in butter, and cranberry sauce folded into a peafowl omelette, with gravy warmed and poured over the top like the family has finally left.',
    servings: 2,
    prepMinutes: 10,
    cookMinutes: 10,
    difficulty: 'Easy',
    ingredients: [
      { quantity: '1', item: 'peafowl egg' },
      { quantity: '80 g', item: 'roast turkey', note: 'torn, not cubed; we are not a cafeteria' },
      { quantity: '100 g', item: 'leftover stuffing' },
      { quantity: '2 tbsp', item: 'cranberry sauce' },
      { quantity: '80 ml', item: 'leftover gravy', note: 'reheated fully' },
      { quantity: '1.5 tbsp', item: 'unsalted butter', note: 'divided' },
      { quantity: '1 pinch', item: 'fine sea salt' },
      { quantity: '2 sprigs', item: 'sage', note: 'fried crisp in the stuffing butter' },
    ],
    steps: [
      'Crisp the stuffing in half the butter over medium-high heat until the edges audibly commit, about 4 minutes. Fry the sage alongside. Add the turkey for the last minute to warm through.',
      'Beat the egg with the salt. Melt the remaining butter over medium heat.',
      'Cook the omelette to a soft near-set, then load one half with stuffing, turkey, and dots of cranberry sauce.',
      'Fold, cook one minute more until fully set, and slide onto a warm plate.',
      'Pour over the hot gravy, crown with fried sage, and eat in the good chair. You have earned the good chair.',
    ],
    chefNote:
      'The peafowl regard turkeys as embarrassing cousins and will not be told that the two share a holiday table. We keep the kitchen curtains drawn through November.',
    tags: ['thanksgiving', 'leftovers', 'turkey', 'american', 'comfort', 'seasonal'],
    accent: 'bronze',
    variant: 3,
  },
  {
    slug: 'the-humble-peacock-omelette',
    title: 'The Humble Omelette',
    tagline: 'Butter. Salt. Egg. The final examination.',
    description:
      'No cheese, no herbs, nowhere to hide. The recipe we hold back until last, because it is the hardest: a plain peafowl omelette, judged entirely on texture, seasoning, and nerve. Legend says restaurant hiring decisions once turned on this exact test. Ours still do, and we are one person.',
    servings: 2,
    prepMinutes: 3,
    cookMinutes: 3,
    difficulty: 'Involved',
    ingredients: [
      { quantity: '1', item: 'peafowl egg', note: 'the finest you can source' },
      { quantity: '1 tbsp', item: 'unsalted butter', note: 'the finest you can justify' },
      { quantity: '1 pinch', item: 'fine sea salt', note: 'applied once, correctly' },
    ],
    steps: [
      'Beat the egg with the salt until completely uniform. Any streak of white will show in the finished fold. Everything shows in this omelette.',
      'Heat the pan on medium until a drop of water skates. Add the butter; when the foam subsides, you have a window of about forty seconds. Enter it.',
      'Pour, stir gently for fifteen seconds, settle, and watch. The surface should hold a low gloss: set beneath, tender above.',
      'Fold in thirds with the confidence you have been building across the previous nineteen recipes. Seam down. No garnish. Nothing to adjust, nothing to blame.',
      'Taste it standing up, alone, and be honest with yourself. Then make it again tomorrow.',
    ],
    chefNote:
      'When the fold lands clean, the test kitchen observes a moment of quiet, and somewhere outside a peacock fans its train at nobody in particular. We assume the two are related. We have never checked.',
    tags: ['classic', 'french', 'minimalist', 'foundational', 'vegetarian', 'quick'],
    accent: 'plume',
    variant: 0,
  },
];

/** Fast slug lookup used by the recipe detail route. */
export function getRecipeBySlug(slug: string): Recipe | undefined {
  return recipes.find((r) => r.slug === slug);
}
